import { asyncRequest, createMessage } from "helpers";

function parseStoredStore(storedValue) {
  if (!storedValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(storedValue);

    if (typeof parsed === "object" && parsed !== null) {
      return parsed;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Format a store object for analytics events.
 * @param {Object} store
 * @param {string} [selectionMethod] - e.g. `"auto"` | `"manual"`
 * @returns {Object|null}
 */
function formatStoreAnalyticsPayload(store, selectionMethod) {
  if (!store) {
    return null;
  }

  const id =
    store.id == null || store.id === ""
      ? null
      : Number(String(store.id).split("/").pop());

  const payload = {
    ...store,
    id: Number.isNaN(id) ? null : id,
  };

  if (selectionMethod) {
    payload.selection_method = selectionMethod;
  }

  return payload;
}

class PreferredStores {
  constructor() {
    const store = this.customerPreferredStore ?? this.userPreferredStore;

    if (store) {
      this.hydrateExistingStore(store);
      return;
    }

    this.setClosestStoreAsPreferred =
      this.setClosestStoreAsPreferred.bind(this);

    WYATT.PubSub.subscribe(
      WYATT.PubSub.events.storesSorted,
      this.setClosestStoreAsPreferred,
    );
  }

  get store() {
    if (this.selectedStore) {
      return this.selectedStore;
    }

    return this.customerPreferredStore ?? this.userPreferredStore ?? null;
  }

  get customerPreferredStore() {
    return WYATT.shopify.customer.metafields.wyatt.preferredStore;
  }

  get userPreferredStore() {
    return parseStoredStore(
      window.localStorage.getItem(WYATT.config.preferredStore.localStorageKey),
    );
  }

  get cartPreferredStoreNumber() {
    return WYATT.Cart.data.attributes?.store ?? null;
  }

  /**
   * Cart-first sync for a store already known from customer/localStorage.
   * @param {Object} store
   */
  async hydrateExistingStore(store) {
    const ok = await this.persist(store);

    if (!ok) {
      return;
    }

    this.selectedStore = store;

    WYATT.PubSub.publish(WYATT.PubSub.events.preferredStoreChange, store);

    WYATT.Analytics.publish(WYATT.Analytics.events.page_context, {
      selected_store: formatStoreAnalyticsPayload(store),
    });
  }

  /**
   * Cart-first sync. On cart failure, show a shopper message and skip
   * localStorage / customer writes.
   * @param {Object} store
   * @returns {Promise<boolean>}
   */
  async persist(store) {
    const ok = await this.setOnCart(store);

    if (!ok) {
      createMessage(WYATT.t.preferred_store.unable_to_set, { type: "error" });
      return false;
    }

    this.setOnUser(store);
    this.setOnCustomer(store);

    return true;
  }

  async setClosestStoreAsPreferred() {
    if (this.selectedStore) {
      return;
    }

    if (!WYATT.Geolocation.sortedStores) {
      return;
    }

    const traditionalStores = WYATT.Geolocation.sortedStores.filter(
      (store) => store.store_type === "traditional",
    );
    const store = traditionalStores[0];

    if (!store) {
      return;
    }

    const ok = await this.persist(store);

    if (!ok) {
      return;
    }

    WYATT.Analytics.publish(WYATT.Analytics.events.select_store, {
      previous_store: formatStoreAnalyticsPayload(this.selectedStore, "auto"),
      selected_store: formatStoreAnalyticsPayload(store, "auto"),
    });

    this.selectedStore = store;

    WYATT.PubSub.publish(WYATT.PubSub.events.preferredStoreChange, store);
  }

  async setStore(store) {
    const previousStore = this.selectedStore;
    const ok = await this.persist(store);

    if (!ok) {
      return;
    }

    WYATT.Analytics.publish(WYATT.Analytics.events.select_store, {
      previous_store: formatStoreAnalyticsPayload(previousStore, "manual"),
      selected_store: formatStoreAnalyticsPayload(store, "manual"),
    });

    this.selectedStore = store;

    WYATT.PubSub.publish(WYATT.PubSub.events.preferredStoreChange, store);
  }

  setOnCustomer(store) {
    if (!WYATT.shopify.customer.id) {
      return;
    }

    if (store.id === this.customerPreferredStore?.id) {
      return;
    }

    WYATT.shopify.customer.metafields.wyatt.preferredStore = store;

    asyncRequest(WYATT.config.preferredStore.endpoints.updateCustomer, {
      ...{
        method: "POST",
      },
      ...{
        body: JSON.stringify({
          store_metaobject_id: store.id,
        }),
      },
    });
  }

  setOnUser(store) {
    const serializedStore = JSON.stringify(store);

    if (
      serializedStore ===
      window.localStorage.getItem(WYATT.config.preferredStore.localStorageKey)
    ) {
      return;
    }

    window.localStorage.setItem(
      WYATT.config.preferredStore.localStorageKey,
      serializedStore,
    );
  }

  /**
   * @param {Object} store
   * @returns {Promise<boolean>}
   */
  async setOnCart(store) {
    if (String(store.store_number) === String(this.cartPreferredStoreNumber)) {
      return true;
    }

    const result = await WYATT.Cart.update({
      attributes: {
        store: store.store_number,
      },
    });

    return Boolean(result);
  }
}

WYATT.PreferredStores = new PreferredStores();

export default PreferredStores;
